import React, { useState } from "react";
import { FullScreenDialog } from "../core/FullScreenDialog";
import TextField from "@mui/material/TextField";
import { DateTimePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { useCreateSession, useMyProfile } from "@seabrookstudios/pitch2table";
import { formatSessionDate, sessionInstantFromCivilTime } from "@seabrookstudios/pitch2table-core";
import Stack from "@mui/material/Stack";
import Add from "@mui/icons-material/Add";
import ListItemText from "@mui/material/ListItemText";
import { TimezoneSelect } from "../core/TimezoneDropDown";
import { SelectConventionDialog } from "../core/SelectConventionCard";
import { SelectGamesDialog } from "../core/SelectGamesDialog";
import { SelectPublishersDialog } from "../core/SelectPublishersDialog";
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { SessionTypeSelect } from "./SessionTypeSelect";

/**
 * Dialog for adding a new session
 * @param {object} props
 * @param {number|undefined} [props.seededDate] - Initial date timestamp
 * @param {import("@seabrookstudios/pitch2table-core").ConventionId|undefined} [props.seededConventionId] - Convention ID to link
 * @param {string|undefined} [props.seededTimeZone] - Timezone to use for the session
 * @param {import("@seabrookstudios/types").GameId[]} [props.seededGameIds] - Initial game IDs to link
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId[]} [props.seededPublisherIds] - Initial publisher IDs to link
 */
export const AddSessionDialog = ({
  seededDate,
  seededConventionId,
  seededTimeZone,
  seededGameIds = [],
  seededPublisherIds = [],
}) => {
  const { data: profile } = useMyProfile();

  const [type, setType] = useState(/** @type {"Pitching"|"PlayTesting"} */ ("Pitching"));
  const initialZone = seededTimeZone || DateTime.now().zoneName;
  const [timezone, setTimezone] = useState(initialZone);
  const [duration, setDuration] = useState(60);
  const [meetingRoomUrl, setMeetingRoomUrl] = useState('');
  const [gameIds, setGameIds] = useState(seededGameIds);
  const [publisherIds, setPublisherIds] = useState(seededPublisherIds);
  const [conventionId, setConventionId] = useState(seededConventionId);

  const initialValue = seededDate ? seededDate : DateTime.now().toMillis();
  // the one we use for the date controls, in the meeting zone
  const [date, setDate] = useState(DateTime.fromMillis(initialValue, { zone: initialZone }));
  // the one we send to the server
  const [dateMillis, setDateMillis] = useState(initialValue);

  const createSession = useCreateSession();

  const handleSave = async () => {
    if (!dateMillis) {
      return;
    }

    /**
     * @type {import("@seabrookstudios/pitch2table-core").CreateSessionRequest}
     */
    const sessionData = {
      type: type,
      date: dateMillis,
      timezone,
      duration,
      gameIds: gameIds,
      publisherIds: publisherIds,
      conventionId,
      deleted: false,
      meetingRoomUrl: meetingRoomUrl || undefined,
    };

    await createSession.mutateAsync(sessionData);
  };

  const canSave = !dateMillis || createSession.isPending;

  /**
   * @param {DateTime} componentDate
   */
  const onUpdateDate = (componentDate) => {
    if (!componentDate?.isValid) {
      return;
    }
    const instant = sessionInstantFromCivilTime(
      componentDate.year,
      componentDate.month,
      componentDate.day,
      componentDate.hour,
      componentDate.minute,
      timezone
    );
    setDateMillis(instant);
    setDate(DateTime.fromMillis(instant, { zone: timezone }));
  };

  if (!profile) {
    return <Skeleton />;
  }

  return (
    <FullScreenDialog
      id="add-session"
      title="Add Session"
      actionText="SAVE"
      onSaveHandler={handleSave}
      canSave={canSave}
      isBusy={createSession.isPending}
      Icon={Add}
    >
      <Stack spacing={3}>
        <SessionTypeSelect value={type} onChange={setType} />
        <TimezoneSelect value={timezone} onChange={setTimezone} title="Timezone of meeting" />
        <Stack direction="row">
          <DateTimePicker
            defaultValue={date}
            label="When"
            onChange={onUpdateDate}
            timezone={timezone}
          />
          <Box justifyContent="center" alignItems="center" display="flex" flex={1}>
            <Typography>{formatSessionDate(dateMillis, timezone)}</Typography>
          </Box>
        </Stack>
        {profile.location?.timezone !== timezone && (
          <ListItemText>
            <ListItemText
              primary="When (in your timezone)"
              secondary={formatSessionDate(dateMillis, profile.location.timezone)}
            />
          </ListItemText>
        )}
        <TextField
          fullWidth
          type="number"
          label="Duration (minutes)"
          value={duration}
          onChange={(e) => setDuration(parseInt(e.target.value, 10))}
        />
        <TextField
          fullWidth
          type="url"
          label="Meeting Room URL"
          value={meetingRoomUrl}
          onChange={(e) => setMeetingRoomUrl(e.target.value)}
          placeholder="https://meet.example.com/..."
        />
        <SelectConventionDialog value={conventionId} onChange={setConventionId} />
        <SelectGamesDialog value={gameIds} onChange={setGameIds} defaultSelectedStatuses={["PitchingNow"]} />
        <SelectPublishersDialog value={publisherIds} onChange={setPublisherIds} />
      </Stack>
    </FullScreenDialog>
  );
};
